/**
 * Agent operations — create, list, delete persistent agents, and read an agent's
 * pinned config / workspace memberships. An agent is a real `agt-` user the server
 * logs in on behalf of when a DM, email or scheduled task wakes it.
 */

import type { ArbiClient, components } from '@arbidocs/client'
import { requireData } from '../errors.js'

type UserResponse = components['schemas']['UserResponse']
type CreateAgentRequest = components['schemas']['CreateAgentRequest']
type CreateAgentResponse = components['schemas']['CreateAgentResponse']
type AgentWorkspaceResponse = components['schemas']['AgentWorkspaceResponse']

export async function listAgents(arbi: ArbiClient): Promise<UserResponse[]> {
  return requireData(await arbi.fetch.GET('/v1/agents'), 'Failed to fetch agents')
}

/**
 * Create a persistent agent. Only `name` is accepted (alphanumeric, `_`/`-`).
 * The keypair is server-generated; `signing_private_key` is returned ONCE.
 * Pin a behaviour by saving a config with this agent's `agent_ext_id`.
 */
export async function createAgent(arbi: ArbiClient, name: string): Promise<CreateAgentResponse> {
  const body: CreateAgentRequest = { name }
  return requireData(await arbi.fetch.POST('/v1/agents', { body }), 'Failed to create agent')
}

/** Read an agent's pinned configuration (its `settings.last_config`). */
export async function getAgentConfig(arbi: ArbiClient, agentExtId: string) {
  return requireData(
    await arbi.fetch.GET('/v1/agents/{agent_ext_id}/config', {
      params: { path: { agent_ext_id: agentExtId } },
    }),
    'Failed to fetch agent config'
  )
}

/** List the workspaces an agent is a member of. */
export async function listAgentWorkspaces(
  arbi: ArbiClient,
  agentExtId: string
): Promise<AgentWorkspaceResponse[]> {
  return requireData(
    await arbi.fetch.GET('/v1/agents/{agent_ext_id}/workspaces', {
      params: { path: { agent_ext_id: agentExtId } },
    }),
    'Failed to fetch agent workspaces'
  )
}

export async function deleteAgents(arbi: ArbiClient, agentExtIds: string[]) {
  return requireData(
    await arbi.fetch.DELETE('/v1/agents', {
      body: { agent_ext_ids: agentExtIds },
    }),
    'Failed to delete agents'
  )
}
